function toHex(n: number) {
    if (!Number.isFinite(n) || n < 0) throw new Error("Only non-negative integers");
    return Math.floor(n).toString(16);
}
function fromHex(hex: string) {
    if (!/^[0-9a-f]+$/i.test(hex)) throw new Error("Bad hex");
    return parseInt(hex, 16);
}

// simple deterministic mixing into 32-bit
function mix32(a: number, b: number) {
    let x = (a >>> 0) ^ ((b >>> 0) * 0x9e3779b1 >>> 0);
    x ^= x >>> 16; x = Math.imul(x, 0x7feb352d) >>> 0;
    x ^= x >>> 15; x = Math.imul(x, 0x846ca68b) >>> 0;
    x ^= x >>> 16;
    return x >>> 0;
}

// xorshift32 PRNG
function makePRNG(seed: number) {
    let x = seed >>> 0 || 0xdeadbeef;
    return function nextU32() {
        x ^= (x << 13) >>> 0;
        x ^= x >>> 17;
        x ^= (x << 5) >>> 0;
        return x >>> 0;
    };
}

// deterministic generation of hex string with fixed length
function randHexDet(len: number, seed: number, salt = 0) {
    const needBytes = len / 2;
    const out = new Uint8Array(needBytes);
    const prng = makePRNG(mix32(seed, salt >>> 0));
    let i = 0, buf = 0, avail = 0;
    while (i < needBytes) {
        if (avail === 0) { buf = prng(); avail = 4; }
        out[i++] = buf & 0xff; buf >>>= 8; avail--;
    }
    return Array.from(out, b => b.toString(16).padStart(2, "0")).join("");
}

// Format: 8hex - {hex(userId)} - 4hex - {hex(courseId)} - 12hex
export function encodeCertificateId(userId: number, courseId: number) {
    const userHex = toHex(userId);
    const courseHex = toHex(courseId);
    // derive a common seed from (userId, courseId)
    const seed = mix32(userId >>> 0, courseId >>> 0);
    const part1 = randHexDet(8, seed, 0); // 8 characters
    const part3 = randHexDet(4, seed, 1); // 4 characters
    const part5 = randHexDet(12, seed, 2); // 12 characters
    return `${part1}-${userHex}-${part3}-${courseHex}-${part5}`;
}

export function decodeCertificateId(certId: string) {
    const parts = certId.split("-");
    if (parts.length !== 5) throw new Error("Bad format");
    // parts[0]=8hex, parts[2]=4hex, parts[4]=12hex are ignored when decoding
    const userId = fromHex(parts[1]);
    const courseId = fromHex(parts[3]);
    return { userId, courseId };
}

// Roadmap certificates reuse the same ID format but offset the entity id so a
// roadmap cert can never collide with a course cert (and so the backend course
// lookup safely 404s instead of returning a wrong course).
export const ROADMAP_CERT_OFFSET = 0x40000000; // 1_073_741_824

export function encodeRoadmapCertificateId(userId: number, roadmapId: number) {
    return encodeCertificateId(userId, roadmapId + ROADMAP_CERT_OFFSET);
}

export function decodeRoadmapCertificateId(certId: string) {
    const { userId, courseId } = decodeCertificateId(certId);
    return { userId, roadmapId: courseId - ROADMAP_CERT_OFFSET };
}

export function isRoadmapCertificateId(certId: string) {
    try {
        return decodeCertificateId(certId).courseId >= ROADMAP_CERT_OFFSET;
    } catch {
        return false;
    }
}